// Truth-dimension and operational state labels (DESIGN_SYSTEM.md §10.7, §10.9, §15).
// Every state carries text and a shape unique to its category, so colour is
// never the only signal; the shape element is aria-hidden and the text is the
// accessible name.

import { REMEDIATION_LABELS as REMEDIATION, RETEST_LABELS as RETEST } from './truthLabels.ts';
import styles from './TruthLabel.module.css';

export type OriginState = 'SOURCE_REPORTED' | 'MODEL_INFERRED' | 'ANALYST_AUTHORED';
export type ReviewState = 'UNREVIEWED' | 'NEEDS_EVIDENCE' | 'ANALYST_VERIFIED' | 'ANALYST_REJECTED';
export type RemediationState =
  'NOT_STARTED' | 'IN_PROGRESS' | 'AWAITING_RETEST' | 'ACCEPTED_RISK' | 'COMPLETED';
export type RetestState =
  'NOT_RECORDED' | 'FIXED' | 'STILL_VULNERABLE' | 'INCONCLUSIVE' | 'UNABLE_TO_TEST';

export type TruthDimension = 'origin' | 'review' | 'remediation' | 'retest';

export type SeverityState = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFORMATIONAL' | 'UNKNOWN';

export type RunState =
  | 'PROPOSED'
  | 'AWAITING_APPROVAL'
  | 'READY'
  | 'RUNNING'
  | 'QUEUED'
  | 'COMPLETED'
  | 'PARTIAL'
  | 'BLOCKED'
  | 'CANCELLED'
  | 'STOP_REQUESTED'
  | 'FAILED'
  | 'STALLED';

export type AttentionState =
  'NEEDS_EVIDENCE' | 'AWAITING_APPROVAL' | 'AWAITING_RETEST' | 'STALE_DATA';

export type DiscoveryState =
  | 'NOT_STARTED'
  | 'RUNNING'
  | 'COMPLETED_NO_FINDINGS'
  | 'COMPLETED_WITH_FINDINGS'
  | 'PARTIAL'
  | 'BLOCKED';

const ORIGIN: Record<OriginState, string> = {
  SOURCE_REPORTED: 'Source-reported',
  MODEL_INFERRED: 'AI-suggested',
  ANALYST_AUTHORED: 'Analyst-authored',
};
const REVIEW: Record<ReviewState, string> = {
  UNREVIEWED: 'Unreviewed',
  NEEDS_EVIDENCE: 'Needs evidence',
  ANALYST_VERIFIED: 'Analyst-verified',
  ANALYST_REJECTED: 'Analyst-rejected',
};

const SEVERITY: Record<SeverityState, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  INFORMATIONAL: 'Informational',
  UNKNOWN: 'Unknown severity',
};

const RUN: Record<RunState, string> = {
  PROPOSED: 'Proposed',
  AWAITING_APPROVAL: 'Awaiting approval',
  READY: 'Ready',
  RUNNING: 'Running',
  QUEUED: 'Queued',
  COMPLETED: 'Completed',
  PARTIAL: 'Partially complete',
  BLOCKED: 'Blocked',
  CANCELLED: 'Cancelled',
  STOP_REQUESTED: 'Stop requested',
  FAILED: 'Failed',
  STALLED: 'Stalled',
};

const ATTENTION: Record<AttentionState, string> = {
  NEEDS_EVIDENCE: 'Needs evidence',
  AWAITING_APPROVAL: 'Awaiting approval',
  AWAITING_RETEST: 'Awaiting re-test',
  STALE_DATA: 'Stale data',
};

const DISCOVERY: Record<DiscoveryState, string> = {
  NOT_STARTED: 'Not started',
  RUNNING: 'Discovering scope',
  COMPLETED_NO_FINDINGS: 'Completed · 0 findings recorded',
  COMPLETED_WITH_FINDINGS: 'Completed with findings',
  PARTIAL: 'Partially complete',
  BLOCKED: 'Discovery blocked',
};

/** Shape is unique per dimension (§4.7): square, circle, triangle, hexagon. */
const DIMENSION_SHAPE: Record<TruthDimension, string> = {
  origin: '■',
  review: '●',
  remediation: '▲',
  retest: '⬡',
};

const DIMENSION_NAME: Record<TruthDimension, string> = {
  origin: 'Origin',
  review: 'Review',
  remediation: 'Remediation',
  retest: 'Re-test',
};

const SEVERITY_SHAPE: Record<SeverityState, string> = {
  CRITICAL: '◆',
  HIGH: '▲',
  MEDIUM: '■',
  LOW: '▼',
  INFORMATIONAL: '●',
  UNKNOWN: '?',
};

const RUN_SHAPE: Record<RunState, string> = {
  PROPOSED: '○',
  AWAITING_APPROVAL: '◷',
  READY: '▷',
  RUNNING: '▶',
  QUEUED: '◷',
  COMPLETED: '✓',
  PARTIAL: '◐',
  BLOCKED: '⚿',
  CANCELLED: '■',
  STOP_REQUESTED: '■',
  FAILED: '✕',
  STALLED: '!',
};

const ATTENTION_SHAPE: Record<AttentionState, string> = {
  NEEDS_EVIDENCE: '◷',
  AWAITING_APPROVAL: '◷',
  AWAITING_RETEST: '◷',
  STALE_DATA: '◷',
};

const DISCOVERY_SHAPE: Record<DiscoveryState, string> = {
  NOT_STARTED: '○',
  RUNNING: '▶',
  COMPLETED_NO_FINDINGS: '✓',
  COMPLETED_WITH_FINDINGS: '●',
  PARTIAL: '◐',
  BLOCKED: '⚿',
};

export interface TruthLabelProps {
  dimension: TruthDimension;
  state: OriginState | ReviewState | RemediationState | RetestState;
  compact?: boolean;
  /** Omit the "<Dimension>:" prefix where a column header or field label already names it. */
  bare?: boolean;
}

/** Renders "<Dimension>: <State text>" plus the dimension's aria-hidden shape. */
export function TruthLabel({ dimension, state, compact, bare }: TruthLabelProps) {
  const label =
    dimension === 'origin'
      ? ORIGIN[state as OriginState]
      : dimension === 'review'
        ? REVIEW[state as ReviewState]
        : dimension === 'remediation'
          ? REMEDIATION[state as RemediationState]
          : RETEST[state as RetestState];
  return (
    <span
      className={[styles.truthLabel, compact ? styles.compact : ''].filter(Boolean).join(' ')}
      data-kind="truth"
      data-dimension={dimension}
      data-state={state}
    >
      <span className={styles.truthShape} aria-hidden="true">
        {DIMENSION_SHAPE[dimension]}
      </span>
      <span className={styles.truthText}>
        {bare ? label : `${DIMENSION_NAME[dimension]}: ${label}`}
      </span>
    </span>
  );
}

export type StateLabelProps =
  | {
      kind: 'truth';
      dimension: TruthDimension;
      state: OriginState | ReviewState | RemediationState | RetestState;
      compact?: boolean;
    }
  | {
      kind: 'severity';
      state: SeverityState;
      compact?: boolean;
    }
  | {
      kind: 'run';
      state: RunState;
      compact?: boolean;
    }
  | {
      kind: 'attention';
      state: AttentionState;
      compact?: boolean;
    }
  | {
      kind: 'discovery';
      state: DiscoveryState;
      compact?: boolean;
    };

/**
 * Shared state label for truth dimensions, severity, run states, and attention states.
 * Guarantees visual text, distinct shape, and WCAG AA contrast in both themes.
 */
export function StateLabel(props: StateLabelProps) {
  if (props.kind === 'truth') {
    return <TruthLabel dimension={props.dimension} state={props.state} compact={props.compact} />;
  }

  let text = '';
  let shape = '';

  if (props.kind === 'severity') {
    text = SEVERITY[props.state];
    shape = SEVERITY_SHAPE[props.state];
  } else if (props.kind === 'run') {
    text = RUN[props.state];
    shape = RUN_SHAPE[props.state];
  } else if (props.kind === 'attention') {
    text = ATTENTION[props.state];
    shape = ATTENTION_SHAPE[props.state];
  } else if (props.kind === 'discovery') {
    text = DISCOVERY[props.state];
    shape = DISCOVERY_SHAPE[props.state];
  }

  return (
    <span
      className={[styles.truthLabel, props.compact ? styles.compact : ''].filter(Boolean).join(' ')}
      data-kind={props.kind}
      data-state={props.state}
    >
      <span className={styles.truthShape} aria-hidden="true">
        {shape}
      </span>
      <span className={styles.truthText}>{text}</span>
    </span>
  );
}
